// /*
//     This will be the Live Track page

//     You can see the table for pass times / estimated times etc

//     There will be a boolean passed for editing - so this component will be able to be used for both editing and viewing only
//         -May have to configure user context with this as well for permissions

//     Basically the same as old VM

//     Editing Version allows selection of pass times for field workers

//     using a hook for consistant polling from the database so any new data comes in and added to table component
// */
import { useEffect, useState, useRef } from "react";
//components
import PassTimeSelect from "../components/ui/modals/PassTimeSelect";
import LiveTrackTable from "../components/tables/LiveTrackTable";
import { Button } from "../components/ui/buttons/Button";
import { PageHeader } from "../components/ui/layout/PageHeader";
import { SelectContainer } from "../components/ui/layout/SelectContainer";
import { PageContainer } from "../components/ui/layout/PageContainer";
import Label from "../components/ui/labels/Label";
import Select from "react-select";
import { LiveTrackSummary } from "../components/liveTrack/LiveTrackSummary";
//hooks
import useRunOptions from "../hooks/useRunOptions";
import useJobOptions from "../hooks/useJobOptions";
import useLiveTrackData from "../hooks/useLiveTrackData";
//context
import { useError } from "../contexts/ErrorContext";
import { useUser } from "../contexts/UserContext";
import { RunMessagesProvider } from "../contexts/runMessages/RunMessageContext";
//api
import { getRunByIDAPI } from "../services/runsAPI";
import { updateAGMsAPI } from "../services/agmsAPI";
import { LiveTrackControl } from "../components/liveTrack/liveTrackControls/LiveTrackControl";
import { RunMessages } from "../components/messages/RunMessages";
//utils
import { fetchJobData } from "@utils/jobs/fetchJobData";

const customStyles = {
    control: (provided) => ({
        ...provided,
        zIndex: 15,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 16,
    }),
};

/**
 * User can view and edit live data from an active run
 * 
 * @returns {JSX.Element}
 * 
 * @category Pages
 */
export const LiveTrackEdit = () => {
    const {
        formattedLiveTrackTable,
        fetchLiveTrackData,
        checkLiveTrackForUpdates,
        findAndStoreLastUpdateValue,
        handleSaveData,
        runData,
        setRunData,
        runMessageCount,
        notificationsPaused,
        setNotificationsPaused
    } = useLiveTrackData();

    const [agmData, setAgmData] = useState([]);
    const [showPassTimeSelect, setShowPassTimeSelect] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedJobOption, setSelectedJobOption] = useState(null);
    const [selectedRunOption, setSelectedRunOption] = useState(null);
    const [initialLaunch, setInitialLaunch] = useState(false);

    const [dataUpdated, setDataUpdated] = useState(new Date()) //update every 10s interval, and when pass time entered, so live track summary updates in sync

    const { jobNameOptions } = useJobOptions();
    const { runNameOptions, fetchRuns, clearRuns } = useRunOptions();
    const { user, storedJob, setStoredJob, userPermissions } = useUser();

    const hasJobPermissions = userPermissions.jobs.create && userPermissions.jobs.update

    const { showError } = useError();

    const clearStatestoDefaults = () => {
        setSelectedRow(null);
        setSelectedRunOption(null);
        setRunData(null);
        setInitialLaunch(false);
        clearRuns();
        setAgmData([]);
    };

    const jobSelectedGetRuns = (job) => {
        fetchJobData(job, showError, setSelectedJobOption, setStoredJob)
        clearStatestoDefaults();
    };

    useEffect(() => {
        if (selectedJobOption) {
            fetchRuns(selectedJobOption.value.toString());
        }
    }, [selectedJobOption]); // eslint-disable-line

    const runSelectedGetLiveTrackData = (run) => {
        setSelectedRunOption(run);
    };

    const getRunData = async () => {
        if (selectedRunOption) {
            let response = await getRunByIDAPI(selectedRunOption.value.toString());
            if (response.success) {
                setRunData(response.data);
            }
        }
    };

    useEffect(() => {
        if (selectedRunOption) {
            getRunData();
            fetchLiveTrackData(selectedRunOption.value);
        }
    }, [selectedRunOption]); // eslint-disable-line

    // Sync agmData with formattedLiveTrackTable for local validation logic
    useEffect(() => {
        setAgmData([...formattedLiveTrackTable]);
        findAndStoreLastUpdateValue();
    }, [formattedLiveTrackTable]); // eslint-disable-line

    const handleLaunchPigClick = () => {
        setInitialLaunch(true);
        setSelectedRow(agmData[0]);
        setShowPassTimeSelect(true);
        setDataUpdated(new Date()) //let summary component know it should update as well
    };


    const handleCellClick = (row) => {
        //check to see if it's launch that is being adjusted - should be able to adjust the initial speed and time and date
        //need to show Initial Launch dialog with any data that may be there.
        const index = agmData.findIndex(agmRow => agmRow.ALTID === row.ALTID);
        if (index === 0) {
            //checks to see if there are any passtimes ahead of launch before you can edit launch
            for (let i = 1; i < agmData.length; i++) {
                // console.log(agmData[i].Speed)
                if (agmData[i].PassTime) {
                    showError(`Cannot Edit Launch while there are AGM Pass Times`);
                    return;
                }
            }
            setInitialLaunch(true);
        }
        setSelectedRow(row);
        setShowPassTimeSelect(true);
    }

    const handleClosePassTimeSelect = () => {
        setInitialLaunch(false);
        setShowPassTimeSelect(false);
        setSelectedRow(null);

    }

    useEffect(() => {
        if (!selectedRunOption) return;
        // Reset initial fetch guard whenever the run changes
        hasFetchedUpdatesRef.current = false;
    }, [selectedRunOption]);

    useEffect(() => {
        if (!selectedRunOption || agmData.length === 0 || hasFetchedUpdatesRef.current) return;

        // Initial fetch so components relying on run data / message counts render immediately.
        checkLiveTrackForUpdates(selectedRunOption.value.toString());
        setDataUpdated(new Date()); // let summary component know it should update as well

        hasFetchedUpdatesRef.current = true;
    }, [selectedRunOption, agmData]);

    useEffect(() => {
        if (!selectedRunOption) return;

        const interval = setInterval(() => {
            // Skip polling while a save is in flight
            if (isSavingRef.current) {
                return;
            }
            checkLiveTrackForUpdates(selectedRunOption.value.toString());
            setDataUpdated(new Date()); //let summary component know it should update as well
        }, 10000); // 10000 milliseconds = 10 seconds

        return () => {
            clearInterval(interval);
        };
    }, [selectedRunOption]); // eslint-disable-line

    const handleSavePassTime = async (dateAndTime, initialSpeed, initialLaunch) => {
        if (selectedRow) {
            const index = agmData.findIndex(row => row.ALTID === selectedRow.ALTID);
            if (index === -1) return;

            //need to check that there is a Launch time and speed - if not cannot enter
            //assuming agmData is sorted
            if (!initialLaunch && (!agmData[0].PassTime || !agmData[0].Speed)) {
                if (index !== 0) {
                    showError(`Launch must have a passtime and speed before any other passtimes can be entered`);
                    return;
                }
            }

            //Make sure there is no passtimes ahead in time from one being entererd
            for (let i = index + 1; i < agmData.length; i++) {
                if ((agmData[i].PassTime && agmData[i].Approved) && new Date(dateAndTime) >= new Date(agmData[i].PassTime)) {
                    showError('Cannot set a PassTime that is before a future PassTime');
                    return;
                }
            }

            //check to see the time being entered isn't before a time previous
            if (dateAndTime) {
                if (index !== 0) {
                    for (let i = index-1; i >= 0; i--) { //index-1 -> Can change the agm that was selected and then checks backwards to see if it is logical
                        if ((agmData[i].PassTime && agmData[i].Approved) && new Date(dateAndTime) <= new Date(agmData[i].PassTime)) {
                            showError(`${agmData[i].AGMs[0].Site_Name} has a Pass Time that is after the time that was entered`);
                            return;
                        }
                    }
                }
            }

            let UtcTime;
            if (dateAndTime) {
                let now_utc = Date.UTC(dateAndTime.getUTCFullYear(), dateAndTime.getUTCMonth(),
                    dateAndTime.getUTCDate(), dateAndTime.getUTCHours(),
                    dateAndTime.getUTCMinutes(), dateAndTime.getUTCSeconds());

                UtcTime = new Date(now_utc).toISOString();
            }

            // Pause polling during save
            isSavingRef.current = true;
            try {
                await handleSaveData(UtcTime, initialSpeed, initialLaunch, selectedRow, setInitialLaunch, runData, setRunData, user.UserID);
            } finally {
                isSavingRef.current = false;
            }

            //tell live track summary to update
            setDataUpdated(new Date())

        }
    };

    const changesRef = useRef({});

    // Track when a save is in flight to pause polling
    const isSavingRef = useRef(false);
    // Ensure initial “updates” fetch only runs once per selected run (not on every agmData change)
    const hasFetchedUpdatesRef = useRef(false);

    const handleCellChange = (updatedAGM) => {
        // console.log('[LiveTrackEdit] handleCellChange called:', { AGMID: updatedAGM.AGMID, updatedAGM });
        // Update ref synchronously so blur always sees latest
        changesRef.current = {
            ...changesRef.current,
            [updatedAGM.AGMID]: updatedAGM,
        };
    };

    const handleCellBlur = async (updatedAGM) => {
        // console.log('[LiveTrackEdit] handleCellBlur called:', { AGMID: updatedAGM.AGMID, hasChanges: !!changesRef.current[updatedAGM.AGMID] });
        // Read from ref to get latest pending change (avoids stale state)
        const pending = changesRef.current[updatedAGM.AGMID] || updatedAGM;
        if (changesRef.current[updatedAGM.AGMID]) {
            // console.log('[LiveTrackEdit] Saving changes for AGMID:', updatedAGM.AGMID);
            await saveChanges(pending);
        } else {
            // console.log('[LiveTrackEdit] No changes to save for AGMID:', updatedAGM.AGMID);
        }
    };

    const saveChanges = async (agm) => {
        const agmId = agm.AGMID;
        const siteName = agm.AGMs?.[0]?.Site_Name || 'Unknown';

        const changedAGM = {
            ...agm,
            ...agm.AGMs[0]
        }

        const clearPendingChange = () => {
            delete changesRef.current[agmId];
        };

        // Pause polling during save
        isSavingRef.current = true;
        try {
            const response = await updateAGMsAPI([changedAGM]);
            if (!response.success) {
                throw new Error("Failed to update AGMs");
            } else {
                clearPendingChange();
            }
        } catch (error) {
            console.error("[LiveTrackEdit] saveChanges - Failed to save input:", { error, agm: changedAGM });
            clearPendingChange();
            window.alert(`Failed to save changes for ${siteName}. Please retry.`);
        } finally {
            isSavingRef.current = false;
        }
    }

    useEffect(() => {
        document.title = `MarTrak - Live Track (Edit)`;

        //if a job was previously selected and added to UserContext, pull it in here
        if(storedJob){
            setSelectedJobOption(storedJob)
            fetchJobData(storedJob, showError, setSelectedJobOption, setStoredJob)
        }
    }, []);//eslint-disable-line

    return (
        <>
            <PageHeader pageTitle="Live Track (Edit)" />
            <PageContainer>
                <Label>Select A Job</Label>
                <SelectContainer>
                    <Select
                        options={jobNameOptions}
                        onChange={jobSelectedGetRuns}
                        styles={customStyles}
                        value={selectedJobOption}
                    />
                </SelectContainer>
                <Label>Select A Run</Label>
                <SelectContainer>
                    <Select
                        options={runNameOptions}
                        onChange={runSelectedGetLiveTrackData}
                        styles={customStyles}
                        value={selectedRunOption}
                    />
                </SelectContainer>

                {/* show live track data if run selected and launched */}
                <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                    {selectedJobOption && 
                    selectedRunOption && 
                    runData && 
                    runData.HasLaunched &&
                    (
                        // run messages area inside of RunMessages context, in order to share messages state
                        <RunMessagesProvider
                            setNotificationsPaused={setNotificationsPaused}
                        >
                            <LiveTrackSummary 
                                selectedJob={selectedJobOption.value}
                                selectedRun={selectedRunOption.value}
                                dataUpdated={dataUpdated}
                                timezone={selectedJobOption.timezone}
                            />

                            <div>
                                <RunMessages
                                    selectedRun={selectedRunOption.value} 
                                    numberOfMessages={runMessageCount}
                                    dataUpdated={dataUpdated}
                                    notificationsPaused={notificationsPaused}
                                    timezone={selectedJobOption.timezone}
                                />

                                <LiveTrackControl 
                                    selectedRun={selectedRunOption.value}
                                    notificationsPaused={notificationsPaused}
                                    setNotificationsPaused={setNotificationsPaused}
                                />

                            </div>
                            
                        </RunMessagesProvider>
                    )}
                </div>
                

                {runData && !runData.HasLaunched && agmData.length > 0 && selectedJobOption.IsLocked !== false && (
                    <div className="w-40 my-10">
                        <Button onClick={handleLaunchPigClick}>Launch Pig</Button>
                    </div>
                )}

                {
                    selectedJobOption && selectedJobOption.IsLocked === false ? (
                        <div className='flex justify-center mt-10'>
                            <p className='text-purple-600 font-bold italic'>
                                {hasJobPermissions ? 
                                'Job is unlocked. To view this page, go to Job Details and lock the job.' : 
                                'Job is still being set up. Please check back later.'}
                            </p>
                        </div>
                    ) : selectedJobOption && selectedRunOption ? (
                    <>

                        <hr className='mb-10'></hr>

                        <LiveTrackTable
                            agmData={formattedLiveTrackTable}
                            isEditable={runData && runData.HasLaunched ? true : false}
                            onCellClick={handleCellClick}
                            onCellChange={handleCellChange}
                            onCellBlur={handleCellBlur}
                            timezone={selectedJobOption.timezone}
                            jobName={selectedJobOption.label}
                            runName={selectedRunOption.label}
                            notificationsPaused={notificationsPaused}
                        />
                        <PassTimeSelect
                            show={showPassTimeSelect}
                            handleClose={handleClosePassTimeSelect}
                            handleSave={handleSavePassTime}
                            row={selectedRow}
                            initialLaunch={initialLaunch}
                            timezone={selectedJobOption.timezone}

                        />
                    </>
                    ) : null
                }
                
            </PageContainer>
        </>
    );
};
